/**
 * InspectorCodeTab — Code editor tab for the NodeInspectorPanel.
 *
 * Handles both editable transforms (SQL/Python with edit/save/preview)
 * and read-only DDL viewers (for target_table nodes).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Code as CodeIcon,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { useWorkspaceQuery } from '../../hooks/useWorkspaceSession';
import type { WorkspaceOperation, WorkspaceTable } from '../../services/workspaceApi';
import { updateOperationConfig } from '../../services/workspaceApi';
import type { PipelineNode } from './DAGCanvas';
import { getApiErrorMessage } from '../../services/apiErrors';
import { WORKSPACE_MONACO_EDITOR_DEFAULTS } from './workspaceMonacoDefaults';
import { useMonacoTableCompletion } from './hooks/useMonacoTableCompletion';

// ── Types ────────────────────────────────────────────────────────────

interface InspectorCodeTabProps {
  selectedOperation: WorkspaceOperation | null;
  selectedNode: PipelineNode;
  sessionId: string;
  codeLanguage: 'sql' | 'python';
  codeContent: string;
  hasCode: boolean;
  pipelineExists?: boolean;
  schemaWarning?: {
    severity: 'error' | 'warning' | 'info';
    message: string;
    brokenColumns?: string[];
  };
  onAskAgent?: (instruction: string) => void;
  workspaceTables: WorkspaceTable[];
  /** Called after code is saved and re-executed (to refresh the Data tab). */
  onCodeSaved?: () => void;
}

// ── Component ────────────────────────────────────────────────────────

const InspectorCodeTab: React.FC<InspectorCodeTabProps> = ({
  selectedOperation,
  selectedNode,
  sessionId,
  codeLanguage,
  codeContent,
  hasCode,
  pipelineExists,
  schemaWarning,
  onAskAgent,
  workspaceTables,
  onCodeSaved,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  // ── Code editing state ─────────────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false);
  const [editableCode, setEditableCode] = useState('');
  const [savedCodeContent, setSavedCodeContent] = useState(codeContent);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [rerunHint, setRerunHint] = useState(false);

  // ── Preview state (Shift+Enter) ────────────────────────────────────
  const [previewExistingLoading, setPreviewExistingLoading] = useState(false);
  const [previewExistingResult, setPreviewExistingResult] = useState<{
    columns: string[];
    rows: Record<string, any>[];
    row_count: number;
  } | null>(null);

  // ── Monaco IntelliSense ────────────────────────────────────────────
  const [codeMonaco, setCodeMonaco] = useState<any>(null);
  const [codeEditorInst, setCodeEditorInst] = useState<any>(null);
  useMonacoTableCompletion(codeMonaco, codeEditorInst, workspaceTables, codeLanguage);

  const workspaceQuery = useWorkspaceQuery(sessionId);

  // Reset editable code when selected operation changes
  useEffect(() => {
    setEditableCode(codeContent);
    setSavedCodeContent(codeContent);
    setIsEditing(false);
    setSaveError(null);
    setPreviewExistingResult(null);
  }, [selectedOperation?.operation_id, codeContent]);

  useEffect(() => {
    if (!codeEditorInst) return;

    codeEditorInst.updateOptions({
      readOnly: !isEditing,
      domReadOnly: !isEditing,
    });

    if (!isEditing) return;

    window.requestAnimationFrame(() => {
      const model = codeEditorInst.getModel?.();
      if (!model) {
        codeEditorInst.focus?.();
        return;
      }

      const lineNumber = model.getLineCount();
      const column = model.getLineMaxColumn(lineNumber);
      codeEditorInst.setPosition?.({ lineNumber, column });
      codeEditorInst.focus?.();
    });
  }, [isEditing, codeEditorInst]);

  // ── Handlers ───────────────────────────────────────────────────────
  const handleSaveCode = useCallback(async () => {
    if (!selectedOperation || !sessionId) return;
    setSaving(true);
    setSaveError(null);
    try {
      const configKey = selectedOperation.config?.sql ? 'sql' : 'function_code';
      const updatedOperation = await updateOperationConfig(
        sessionId,
        selectedOperation.operation_id,
        { [configKey]: editableCode },
        'User edited code in inspector panel',
      );
      const nextCode =
        updatedOperation.config?.[configKey]
        ?? updatedOperation.config?.code
        ?? editableCode;
      setSavedCodeContent(nextCode);
      setEditableCode(nextCode);
      queryClient.setQueryData(
        ['agent-operations', sessionId],
        (existing: WorkspaceOperation[] | undefined) =>
          existing
            ? existing.map((operation) =>
            operation.operation_id === updatedOperation.operation_id ? updatedOperation : operation,
            )
            : [updatedOperation],
      );
      queryClient.invalidateQueries(['agent-operations', sessionId]);
      queryClient.invalidateQueries(['agent-workspace', sessionId]);
      setIsEditing(false);
      onCodeSaved?.();
      if (pipelineExists) {
        setRerunHint(true);
        setTimeout(() => setRerunHint(false), 5000);
      }
    } catch (err: unknown) {
      setSaveError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [selectedOperation, sessionId, editableCode, onCodeSaved, pipelineExists, queryClient]);

  const handleCancelEdit = useCallback(() => {
    setEditableCode(codeContent);
    setIsEditing(false);
    setSaveError(null);
    setPreviewExistingResult(null);
  }, [codeContent]);

  const handlePreviewExisting = useCallback(async () => {
    if (!editableCode.trim()) return;
    setPreviewExistingLoading(true);
    try {
      const result = await workspaceQuery.mutateAsync({ sql: editableCode, limit: 100 });
      setPreviewExistingResult({ columns: result.columns, rows: result.rows, row_count: result.row_count });
    } catch (err: unknown) {
      setSaveError(getApiErrorMessage(err));
    } finally {
      setPreviewExistingLoading(false);
    }
  }, [editableCode, workspaceQuery]);

  // ── Read-only DDL viewer (target_table nodes) ──────────────────────
  if (!hasCode && selectedNode?.codeSnippet) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
            py: 0.5,
            borderBottom: 1,
            borderColor: 'divider',
            gap: 0.5,
          }}
        >
          <Chip
            label="SQL"
            size="small"
            sx={{
              fontSize: 9,
              height: 18,
              bgcolor: alpha('#0891b2', 0.15),
              color: '#0891b2',
              fontWeight: 700,
            }}
          />
          <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
            Generated DDL
          </Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton
            size="small"
            onClick={() => navigator.clipboard.writeText(selectedNode.codeSnippet || '')}
            sx={{ p: 0.5 }}
          >
            <Tooltip title="Copy code">
              <CodeIcon sx={{ fontSize: 14 }} />
            </Tooltip>
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Editor
            height="100%"
            language="sql"
            value={selectedNode.codeSnippet}
            theme="vs-dark"
            wrapperProps={{
              'data-testid': 'node-inspector-ddl-editor',
              'data-editor-state': 'readonly',
            }}
            options={{
              ...WORKSPACE_MONACO_EDITOR_DEFAULTS,
              ariaLabel: 'Generated table DDL, read-only',
              readOnly: true,
              minimap: { enabled: false },
              fontSize: 13,
              fontFamily: '"Fira Code", "Roboto Mono", monospace',
              lineNumbers: 'on' as const,
              scrollBeyondLastLine: false,
              wordWrap: 'on' as const,
              padding: { top: 8 },
              renderLineHighlight: 'none' as const,
              scrollbar: { verticalScrollbarSize: 8 },
            } as any}
          />
        </Box>
      </Box>
    );
  }

  // ── Editable code editor ───────────────────────────────────────────
  if (!hasCode) return null;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
          gap: 0.5,
        }}
      >
        <Chip
          label={codeLanguage.toUpperCase()}
          size="small"
          sx={{
            fontSize: 9,
            height: 18,
            bgcolor: codeLanguage === 'sql' ? alpha('#0891b2', 0.15) : alpha('#7c3aed', 0.15),
            color: codeLanguage === 'sql' ? '#0891b2' : '#7c3aed',
            fontWeight: 700,
          }}
        />
        {selectedOperation?.config_history && selectedOperation.config_history.length > 0 && (
          <Chip
            label={`v${selectedOperation.config_history.length + 1}`}
            size="small"
            sx={{ fontSize: 9, height: 18, bgcolor: alpha('#64748b', 0.1), color: '#64748b' }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {rerunHint && (
          <Typography variant="caption" color="info.main" sx={{ fontSize: 10, mr: 1 }}>
            Pipeline may need re-running
          </Typography>
        )}
        {isEditing ? (
          <>
            <Tooltip title="Shift+Enter">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handlePreviewExisting}
                  disabled={saving || previewExistingLoading}
                  sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 'auto', textTransform: 'none' }}
                >
                  {previewExistingLoading ? <CircularProgress size={12} sx={{ mr: 0.5 }} /> : null}
                  Preview
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Cmd+S">
              <span>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSaveCode}
                  disabled={saving || editableCode === codeContent}
                  sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 'auto', textTransform: 'none' }}
                >
                  {saving ? 'Running...' : 'Save & Run'}
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              onClick={handleCancelEdit}
              disabled={saving}
              sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 'auto', textTransform: 'none' }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            {selectedOperation?.status === 'executed' && (
              <Button
                size="small"
                onClick={() => setIsEditing(true)}
                sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 'auto', textTransform: 'none' }}
              >
                Edit
              </Button>
            )}
            <IconButton
              size="small"
              onClick={() => navigator.clipboard.writeText(codeContent)}
              sx={{ p: 0.5 }}
            >
              <Tooltip title="Copy code">
                <CodeIcon sx={{ fontSize: 14 }} />
              </Tooltip>
            </IconButton>
          </>
        )}
      </Box>
      {saveError && (
        <Alert
          severity="error"
          onClose={() => setSaveError(null)}
          sx={{
            mx: 1,
            mt: 0.5,
            py: 0.5,
            '& .MuiAlert-message': {
              fontSize: 12,
              width: '100%',
              maxHeight: 200,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            },
          }}
        >
          {saveError}
        </Alert>
      )}
      {/* Schema warning banner */}
      {schemaWarning && (
        <Alert
          severity={schemaWarning.severity === 'error' ? 'error' : 'warning'}
          sx={{ mx: 1, mt: 0.5, py: 0.25, '& .MuiAlert-message': { fontSize: 11 } }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
            {schemaWarning.severity === 'error' ? 'Schema Breaking Change' : 'Schema Change Detected'}
          </Typography>
          <Typography sx={{ fontSize: 11 }}>
            {schemaWarning.brokenColumns?.length
              ? <>Column{schemaWarning.brokenColumns.length > 1 ? 's' : ''} <code>{schemaWarning.brokenColumns.join(', ')}</code> no longer exist in the source data. Edit the SQL below or {onAskAgent ? (
                  <Typography
                    component="span"
                    sx={{ fontSize: 11, color: 'primary.main', cursor: 'pointer', textDecoration: 'underline', '&:hover': { textDecoration: 'none' } }}
                    onClick={() => onAskAgent(`Fix broken columns: ${schemaWarning.brokenColumns?.join(', ')}`)}
                  >
                    ask the agent to fix it
                  </Typography>
                ) : 'ask the agent to fix it'}.</>
              : schemaWarning.message}
          </Typography>
        </Alert>
      )}
      {/* Monaco Editor */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Editor
          height="100%"
          language={codeLanguage}
          value={isEditing ? editableCode : savedCodeContent}
          onChange={(value) => isEditing && setEditableCode(value || '')}
          theme="vs-dark"
          wrapperProps={{
            'data-testid': 'node-inspector-code-editor',
            'data-editor-state': isEditing ? 'editable' : 'readonly',
          }}
          onMount={(editor, monaco) => {
            setCodeMonaco(monaco);
            setCodeEditorInst(editor);
            // Shift+Enter → Preview
            editor.addCommand(
              monaco.KeyMod.Shift | monaco.KeyCode.Enter,
              () => handlePreviewExisting(),
            );
            // Cmd+S (Mac) / Ctrl+S (Win) → Save & Run
            editor.addCommand(
              monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
              () => handleSaveCode(),
            );
            // Fix: When the suggest widget is visible, pressing Space
            // dismisses the suggestion and types a space instead of
            // accepting the suggestion (which swallows the space key).
            editor.addCommand(
              monaco.KeyCode.Space,
              () => {
                editor.trigger('keyboard', 'hideSuggestWidget', {});
                editor.trigger('keyboard', 'type', { text: ' ' });
              },
              'suggestWidgetVisible',
            );
            // Safety net: macOS double-space-to-period replacement
            let fixing = false;
            editor.onDidChangeModelContent((e: any) => {
              if (fixing) return;
              for (const change of e.changes) {
                if (change.text === '. ' && change.rangeLength >= 1) {
                  fixing = true;
                  const endCol = change.range.startColumn + 2;
                  editor.executeEdits('fix-macos-period', [{
                    range: new monaco.Range(
                      change.range.startLineNumber, change.range.startColumn,
                      change.range.startLineNumber, endCol,
                    ),
                    text: '  ',
                  }]);
                  fixing = false;
                  break;
                }
              }
            });
          }}
          options={{
            ...WORKSPACE_MONACO_EDITOR_DEFAULTS,
            ariaLabel: isEditing
              ? `${codeLanguage === 'sql' ? 'SQL' : 'Python'} transform code editor`
              : `${codeLanguage === 'sql' ? 'SQL' : 'Python'} transform code editor, read-only; use Edit to change`,
            readOnly: !isEditing,
            domReadOnly: !isEditing,
            minimap: { enabled: false },
            fontSize: 13,
            fontFamily: '"Fira Code", "Roboto Mono", monospace',
            lineNumbers: 'on' as const,
            scrollBeyondLastLine: false,
            wordWrap: 'on' as const,
            padding: { top: 8 },
            renderLineHighlight: isEditing ? 'all' as const : 'none' as const,
            scrollbar: { verticalScrollbarSize: 8 },
            acceptSuggestionOnCommitCharacter: false,
          } as any}
        />
      </Box>
      {/* Inline preview results (Shift+Enter) */}
      {previewExistingResult && isEditing && (
        <Box
          sx={{
            maxHeight: 130,
            overflow: 'auto',
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: alpha('#3b82f6', 0.02),
          }}
        >
          <Box sx={{ px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="Preview" size="small" color="info" variant="outlined" sx={{ fontSize: 9, height: 16 }} />
            <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
              {previewExistingResult.row_count} rows · {previewExistingResult.columns.length} columns
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              onClick={() => setPreviewExistingResult(null)}
              sx={{ fontSize: 9, py: 0, px: 0.5, minWidth: 'auto', textTransform: 'none' }}
            >
              Dismiss
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 100 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {previewExistingResult.columns.slice(0, 8).map((col: string) => (
                    <TableCell key={col} sx={{ fontSize: 10, fontWeight: 700, py: 0.25, px: 1, whiteSpace: 'nowrap', bgcolor: '#f8fafc' }}>
                      {col}
                    </TableCell>
                  ))}
                  {previewExistingResult.columns.length > 8 && (
                    <TableCell sx={{ fontSize: 10, py: 0.25, px: 1, bgcolor: '#f8fafc' }}>+{previewExistingResult.columns.length - 8}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewExistingResult.rows.slice(0, 5).map((row: Record<string, any>, i: number) => (
                  <TableRow key={i}>
                    {previewExistingResult.columns.slice(0, 8).map((col: string) => (
                      <TableCell key={col} sx={{ fontSize: 10, py: 0.25, px: 1, whiteSpace: 'nowrap', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row[col] != null ? String(row[col]) : ''}
                      </TableCell>
                    ))}
                    {previewExistingResult.columns.length > 8 && <TableCell sx={{ fontSize: 10, py: 0.25, px: 1 }}>…</TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {/* Version history strip */}
      {selectedOperation?.config_history && selectedOperation.config_history.length > 0 && (
        <Box
          sx={{
            maxHeight: 100,
            overflow: 'auto',
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.grey[100], 0.5),
            px: 1,
            py: 0.5,
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary' }}>
            Version History
          </Typography>
          {[...selectedOperation.config_history].reverse().map((entry, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                py: 0.25,
              }}
            >
              <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled', minWidth: 100 }}>
                {new Date(entry.edited_at).toLocaleString(undefined, {
                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
              </Typography>
              <Chip
                label={entry.edited_by}
                size="small"
                sx={{ fontSize: 8, height: 14, bgcolor: entry.edited_by === 'user' ? alpha('#3b82f6', 0.1) : alpha('#059669', 0.1) }}
              />
              <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary', flex: 1 }} noWrap>
                {entry.reason || 'No reason'}
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  const key = codeLanguage === 'sql' ? 'sql' : 'function_code';
                  setEditableCode(entry.config?.[key] || '');
                  setIsEditing(true);
                }}
                sx={{ fontSize: 9, py: 0, px: 0.5, minWidth: 'auto', textTransform: 'none' }}
              >
                Revert
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default InspectorCodeTab;
